/**
 * @file promotion-checkout.js
 * @description Checkout flow and SF Express integration
 * @module GniibPromotions.Checkout
 *
 * Contains CheckoutService for checkout orchestration,
 * SFExpressService for popup handling, and DraftOrderService for API calls.
 */
(function(global) {
  'use strict';

  var GP = global.GniibPromotions = global.GniibPromotions || {};
  var Config = GP.Config;
  var Messages = GP.Messages;
  var CartManager = GP.CartManager;

  /**
   * Checkout namespace
   * @namespace GniibPromotions.Checkout
   */
  GP.Checkout = {

    /**
     * Handle checkout with SF Express flow
     * @param {Object} ctx - Promotion context
     */
    handleWithSFExpress: async function(ctx) {
      console.log('[SF Express] handleWithSFExpress called');
      this.setButtonsState(ctx, true, 'processing');

      try {
        console.log('[SF Express] Triggering SF Express selector...');
        if (!this.triggerSFExpressSelector()) {
          console.warn('SF Express button not found, falling back to normal checkout');
          return await this.proceedWithDraftOrder(ctx, null);
        }

        var address = await this.waitForSFExpressSelection(ctx);
        await this.proceedWithDraftOrder(ctx, address);

      } catch (error) {
        if (error.message === 'USER_CANCELLED') {
          console.log('[SF Express] User cancelled address selection');
          this.setButtonsState(ctx, false, 'ready');
        } else {
          console.warn('[SF Express] Integration failed, falling back:', error);
          try {
            await this.proceedWithDraftOrder(ctx, null);
          } catch (fallbackError) {
            console.error('[SF Express] Fallback also failed:', fallbackError);
            // proceedWithDraftOrder has its own error handling, but just in case
            this.setButtonsState(ctx, false, 'ready');
          }
        }
      }
    },

    /**
     * Proceed with draft order creation
     * @param {Object} ctx - Promotion context
     * @param {Object|null} sfExpressAddress - SF Express address or null
     */
    proceedWithDraftOrder: async function(ctx, sfExpressAddress) {
      console.log('[Draft Order] Starting with address:', sfExpressAddress);

      try {
        var customerId = GP.CustomerService.getCustomerId();
        if (!customerId) {
          throw new Error('Customer ID not found');
        }

        var cartLines = CartManager.transformToCartLines(ctx.cart, function(item) {
          return ctx.getDiscountNameForItem(item);
        });

        var discountApplicationName = ctx.getDiscountName();

        var requestBody = {
          customerId: customerId,
          role: ctx.customerRole,
          cartLines: cartLines,
          discountApplicationName: discountApplicationName,
          sfExpressAddress: sfExpressAddress?.value || sfExpressAddress || null,
          sfExpressRegion: sfExpressAddress?.regionIndex || null,
          sfExpressDistrict: sfExpressAddress?.districtIndex || null
        };

        // Include discount code for certain promotions
        if (ctx.promotionType === 'kwh_macau' && ctx.cart.promotionData?.promotion?.discountCode) {
          requestBody.discountCode = ctx.cart.promotionData.promotion.discountCode;
        }

        console.log('[Draft Order] Request body:', requestBody);
        console.log('[Draft Order] Sending API request to:', ctx.apiEndpoint + '/checkout');

        var response = await fetch(ctx.apiEndpoint + '/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        console.log('[Draft Order] API response status:', response.status);

        if (!response.ok) {
          throw new Error('Checkout request failed: ' + response.status);
        }

        var result = await response.json();
        console.log('[Draft Order] Result:', result);

        if (result.success && result.draftOrder?.checkoutUrl) {
          console.log('[Draft Order] Redirecting to:', result.draftOrder.checkoutUrl);

          // Close SF Express popup before redirecting
          var popup = this.findSFExpressPopup();
          if (popup) {
            console.log('[Draft Order] Closing SF Express popup');
            popup.style.display = 'none';
            popup.remove();
          }

          // Cleanup SF Express interceptors
          if (ctx.sfExpressHandlers) {
            console.log('[Draft Order] Cleaning up SF Express interceptors');
            this.removeSFExpressInterceptors(ctx);
          }

          // Remove navigation blocker before redirecting
          if (ctx.navigationBlocker) {
            console.log('[Draft Order] Removing navigation blocker');
            window.removeEventListener('beforeunload', ctx.navigationBlocker);
            ctx.navigationBlocker = null;
          }

          window.location.href = result.draftOrder.checkoutUrl;
        } else {
          throw new Error('Invalid checkout response');
        }

      } catch (error) {
        console.error('[Draft Order] Error:', error);

        if (ctx.navigationBlocker) {
          console.log('[Draft Order] Removing navigation blocker due to error');
          window.removeEventListener('beforeunload', ctx.navigationBlocker);
          ctx.navigationBlocker = null;
        }

        alert(Config.Messages.ERRORS.CHECKOUT_ERROR);
        this.setButtonsState(ctx, false, 'ready');
      }
    },

    /**
     * Trigger SF Express address selector
     * @returns {boolean} True if button found and clicked
     */
    triggerSFExpressSelector: function() {
      console.log('[SF Express] Looking for trigger button...');
      var sfButton = document.querySelector('.ysd-sfexpress-address-selector');
      console.log('[SF Express] Trigger button found:', sfButton);

      if (sfButton) {
        console.log('[SF Express] Clicking trigger button...');
        sfButton.click();
        return true;
      }
      return false;
    },

    /**
     * Wait for SF Express selection
     * @param {Object} ctx - Promotion context
     * @returns {Promise<Object>} Selected address
     */
    waitForSFExpressSelection: function(ctx) {
      var self = this;

      return new Promise(function(resolve, reject) {
        var timeout = setTimeout(function() {
          self.removeSFExpressInterceptors(ctx);
          reject(new Error('TIMEOUT'));
        }, Config.SF_EXPRESS.TIMEOUT);

        function cleanup() {
          clearTimeout(timeout);
          self.removeSFExpressInterceptors(ctx);
        }

        // Call setupSFExpressInterceptors directly - it will poll for buttons
        console.log('[SF Express] Setting up interceptors...');
        self.setupSFExpressInterceptors(ctx, resolve, reject, cleanup);
      });
    },

    /**
     * Find SF Express popup
     * @returns {HTMLElement|null} Popup element or null
     */
    findSFExpressPopup: function() {
      console.log('[SF Express] Looking for popup...');
      var selectors = [
        '#sf-express-popup',
        '.sf-express-modal',
        '[class*="sf-express"]',
        'div:has(#sf-checkout-btn)'
      ];

      for (var i = 0; i < selectors.length; i++) {
        try {
          var popup = document.querySelector(selectors[i]);
          if (popup && popup.offsetParent !== null) {
            return popup;
          }
        } catch (e) {
          continue;
        }
      }

      var sfBtn = document.getElementById('sf-checkout-btn');
      var normalBtn = document.getElementById('normal-checkout-btn');
      if (sfBtn || normalBtn) {
        return (sfBtn || normalBtn).closest('div');
      }

      return null;
    },

    /**
     * Localize SF Express popup content
     * @param {HTMLElement} popup - Popup element
     */
    localizeSFExpressPopupContent: function(popup) {
      var titleElement = popup.querySelector('.shipping-title-6');
      if (!titleElement) return;

      var message = Messages.getSFExpressMessage();
      if (message) {
        titleElement.innerHTML = message.text +
          '<a href="' + message.linkUrl + '" target="_blank" rel="noopener noreferrer">' +
          message.linkText + '</a>';
      }
    },

    /**
     * Setup SF Express interceptors
     * @param {Object} ctx - Promotion context
     * @param {Function} resolve - Promise resolve
     * @param {Function} reject - Promise reject
     * @param {Function} cleanup - Cleanup function
     */
    setupSFExpressInterceptors: function(ctx, resolve, reject, cleanup) {
      var self = this;

      ctx.sfExpressHandlers = {
        sfCheckout: null,
        normalCheckout: null,
        escapeKey: null,
        buttonCheckInterval: null,
        popupClickHandler: null
      };

      var checkButtons = setInterval(function() {
        // Check for buttons FIRST - popup is optional
        var sfBtn = document.getElementById('sf-checkout-btn');
        var normalBtn = document.getElementById('normal-checkout-btn');
        console.log('[SF Express] Polling - sfBtn:', sfBtn, 'normalBtn:', normalBtn);

        if (sfBtn || normalBtn) {
          clearInterval(checkButtons);
          var popup = self.findSFExpressPopup();  // Optional - for event delegation
          console.log('[SF Express] Buttons found, popup:', popup ? 'yes' : 'no');

          // Localize popup content when found
          if (popup) {
            self.localizeSFExpressPopupContent(popup);
          }

          function findParentWithId(element, targetId) {
            var current = element;
            while (current && current !== document.body) {
              if (current.id === targetId) return current;
              current = current.parentElement;
            }
            return null;
          }

          var popupClickHandler = function(e) {
            console.log('[SF Express] Click detected on popup');

            if (findParentWithId(e.target, 'sf-checkout-btn')) {
              console.log('[SF Express] SF checkout button clicked');
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();

              var form = e.target.closest('form') || e.target.form;
              if (form) form.onsubmit = function() { return false; };

              self.installNavigationBlocker(ctx);
              var address = self.getSFExpressAddress();
              console.log('[SF Express] Address extracted:', address);

              cleanup();
              resolve(address);
              return;
            }

            if (findParentWithId(e.target, 'normal-checkout-btn')) {
              console.log('[SF Express] Normal checkout clicked');
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();

              var form = e.target.closest('form') || e.target.form;
              if (form) form.onsubmit = function() { return false; };

              self.installNavigationBlocker(ctx);
              resolve(null);
              return;
            }
          };

          // Only attach popup delegation if popup found
          if (popup) {
            ctx.sfExpressHandlers.popupClickHandler = popupClickHandler;
            popup.addEventListener('click', popupClickHandler, true);
          }

          // Direct button listeners (always attached)
          if (sfBtn) {
            var sfHandler = function(e) {
              console.log('[SF Express] SF checkout button clicked (direct)');
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();

              var form = e.target.closest('form') || e.target.form;
              if (form) form.onsubmit = function() { return false; };

              self.installNavigationBlocker(ctx);
              var address = self.getSFExpressAddress();
              cleanup();
              resolve(address);
            };
            sfBtn.addEventListener('click', sfHandler, true);
            ctx.sfExpressHandlers.sfCheckout = sfHandler;
          }

          if (normalBtn) {
            var normalHandler = function(e) {
              console.log('[SF Express] Normal checkout clicked (direct)');
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();

              var form = e.target.closest('form') || e.target.form;
              if (form) form.onsubmit = function() { return false; };

              self.installNavigationBlocker(ctx);
              resolve(null);
            };
            normalBtn.addEventListener('click', normalHandler, true);
            ctx.sfExpressHandlers.normalCheckout = normalHandler;
          }
        }
      }, 100);

      ctx.sfExpressHandlers.buttonCheckInterval = checkButtons;

      // ESC key handler
      var escapeHandler = function(e) {
        if (e.key === 'Escape') {
          console.log('[SF Express] ESC pressed - user cancelled');
          cleanup();
          reject(new Error('USER_CANCELLED'));
        }
      };
      document.addEventListener('keydown', escapeHandler);
      ctx.sfExpressHandlers.escapeKey = escapeHandler;

      // Close button detection
      setTimeout(function() {
        if (!ctx.sfExpressHandlers) return;

        var closeButtons = document.querySelectorAll('.sf-close-btn, [class*="close"], [aria-label*="close" i]');
        closeButtons.forEach(function(btn) {
          btn.addEventListener('click', function() {
            console.log('[SF Express] Close button clicked');
            cleanup();
            reject(new Error('USER_CANCELLED'));
          });
        });
      }, 500);
    },

    /**
     * Install navigation blocker
     * @param {Object} ctx - Promotion context
     */
    installNavigationBlocker: function(ctx) {
      var blockNavigation = function(evt) {
        evt.preventDefault();
        evt.returnValue = '';
      };
      window.addEventListener('beforeunload', blockNavigation);
      ctx.navigationBlocker = blockNavigation;
      console.log('[SF Express] Navigation blocker installed');
    },

    /**
     * Remove SF Express interceptors
     * @param {Object} ctx - Promotion context
     */
    removeSFExpressInterceptors: function(ctx) {
      if (!ctx.sfExpressHandlers) return;

      if (ctx.sfExpressHandlers.buttonCheckInterval) {
        clearInterval(ctx.sfExpressHandlers.buttonCheckInterval);
      }

      if (ctx.sfExpressHandlers.escapeKey) {
        document.removeEventListener('keydown', ctx.sfExpressHandlers.escapeKey);
      }

      var popup = this.findSFExpressPopup();
      if (popup && ctx.sfExpressHandlers.popupClickHandler) {
        popup.removeEventListener('click', ctx.sfExpressHandlers.popupClickHandler, true);
      }

      var sfBtn = document.getElementById('sf-checkout-btn');
      if (sfBtn && ctx.sfExpressHandlers.sfCheckout) {
        sfBtn.removeEventListener('click', ctx.sfExpressHandlers.sfCheckout, true);
      }

      var normalBtn = document.getElementById('normal-checkout-btn');
      if (normalBtn && ctx.sfExpressHandlers.normalCheckout) {
        normalBtn.removeEventListener('click', ctx.sfExpressHandlers.normalCheckout, true);
      }

      ctx.sfExpressHandlers = null;
    },

    /**
     * Get SF Express address from form
     * @returns {Object|null} Address object or null
     */
    getSFExpressAddress: function() {
      var selectedRadio = document.querySelector('input[name="result"]:checked');

      if (selectedRadio && selectedRadio.value) {
        return {
          value: selectedRadio.value,
          regionIndex: selectedRadio.dataset.regionindex || null,
          districtIndex: selectedRadio.dataset.districtindex || null
        };
      }

      return null;
    },

    /**
     * Set checkout buttons state
     * @param {Object} ctx - Promotion context
     * @param {boolean} disabled - Whether to disable
     * @param {string} state - State name (ready, processing, completed)
     */
    setButtonsState: function(ctx, disabled, state) {
      var stateText = {
        ready: '結帳',
        processing: '處理中...',
        completed: '完成'
      };

      var desktopBtn = ctx.elements.CHECKOUT_BTN;
      var mobileBtn = ctx.elements.MOBILE_CHECKOUT_BTN;

      [desktopBtn, mobileBtn].forEach(function(btn) {
        if (btn) {
          btn.disabled = disabled;
          btn.textContent = stateText[state] || stateText.ready;
        }
      });
    }
  };

})(window);
